import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { useTranslation } from '../i18n';
import {
  GraduationCap, ChevronDown, LogOut, User, Menu, X, Home, Users, Tent, Settings,
} from 'lucide-react';

function NavDropdown({ label, items, icon: Icon, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
          disabled
            ? 'text-slate-300 cursor-not-allowed'
            : 'text-slate-600 hover:text-sky-600 hover:bg-sky-50'
        }`}
      >
        <Icon size={16} />
        {label}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && !disabled && (
        <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-2xl shadow-lg border border-slate-200/60 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          {items.length === 0 ? (
            <p className="px-4 py-3 text-slate-400 text-sm">No enrollments yet</p>
          ) : (
            items.map((item, i) => (
              <button
                key={i}
                onClick={() => { navigate(item.href); setOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-colors"
              >
                <span className="font-medium">{item.label}</span>
                {item.sub && <p className="text-xs text-slate-400 mt-0.5">{item.sub}</p>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { userName, avatarUrl, familyRole, logout, isAuthenticated } = useAuth();
  const { setShowAccountSettings } = useUI();
  const { t } = useTranslation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = userName ? userName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : 'U';

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" data-testid="header-logo" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-sky-500 rounded-xl flex items-center justify-center shadow-sm">
              <GraduationCap size={20} className="text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-slate-900 font-outfit text-base leading-tight block">Learning Mastery</span>
              <span className="text-sky-500 text-xs font-medium">Asia Coaching Portal</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink
              to="/dashboard"
              data-testid="nav-dashboard"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isActive ? 'text-sky-600 bg-sky-50' : 'text-slate-600 hover:text-sky-600 hover:bg-sky-50'
                }`
              }
            >
              <Home size={16} /> {t('nav.dashboard')}
            </NavLink>
            <NavLink
              to="/my-family"
              data-testid="nav-my-family"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isActive ? 'text-sky-600 bg-sky-50' : 'text-slate-600 hover:text-sky-600 hover:bg-sky-50'
                }`
              }
            >
              <Users size={16} /> {t('nav.myFamily')}
            </NavLink>
            <NavLink
              to="/my-camps"
              data-testid="nav-my-camps"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isActive ? 'text-sky-600 bg-sky-50' : 'text-slate-600 hover:text-sky-600 hover:bg-sky-50'
                }`
              }
            >
              <Tent size={16} /> {t('nav.myCamps')}
            </NavLink>
            <NavLink
              to="/my-coaching"
              data-testid="nav-my-coaching"
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isActive ? 'text-sky-600 bg-sky-50' : 'text-slate-600 hover:text-sky-600 hover:bg-sky-50'
                }`
              }
            >
              <GraduationCap size={16} /> {t('nav.myCoaching')}
            </NavLink>          </nav>

          {/* User menu */}
          <div className="flex items-center gap-2">
            <div ref={userMenuRef} className="relative hidden md:block">
              <button
                data-testid="user-menu-btn"
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center gap-2 px-3 py-2 rounded-2xl hover:bg-slate-50 transition-colors"
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt={userName} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-sky-400 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {initials}
                  </div>
                )}
                <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate">{userName}</span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-lg border border-slate-200/60 py-2 z-50">
                  <div className="px-4 py-2 border-b border-slate-100 mb-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{userName}</p>
                    {familyRole && <p className="text-xs text-slate-400 capitalize">{familyRole}</p>}
                  </div>
                  <button
                    data-testid="account-settings-btn"
                    onClick={() => { setShowAccountSettings(true); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                  >
                    <Settings size={15} /> {t('nav.accountSettings')}
                  </button>
                  <button
                    data-testid="logout-btn"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={15} /> {t('nav.signOut')}
                  </button>
                </div>
              )}
            </div>
            {/* Mobile hamburger */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(o => !o)}
              className="md:hidden p-2 rounded-xl hover:bg-slate-50"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-100 py-3 space-y-1">
            {[
              { to: '/dashboard', label: t('nav.dashboard'), icon: Home },
              { to: '/my-family', label: t('nav.myFamily'), icon: Users },
              { to: '/my-camps', label: t('nav.myCamps'), icon: Tent },
              { to: '/my-coaching', label: t('nav.myCoaching'), icon: GraduationCap },
            ].map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    isActive ? 'text-sky-600 bg-sky-50' : 'text-slate-600'
                  }`
                }
              >
                <Icon size={16} /> {label}
              </NavLink>
            ))}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 rounded-xl hover:bg-red-50"
            >
              <LogOut size={16} /> {t('nav.signOut')}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
